import { useParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { getLeaderboard } from '../services/api';
import { formatLeaderboardTime } from '@cloudfruit-hunts/shared';

export function LeaderboardPage() {
  const { huntId } = useParams<{ huntId: string }>();

  const { data, isLoading, error } = useQuery({
    queryKey: ['leaderboard', huntId],
    queryFn: () => getLeaderboard(huntId!),
    enabled: !!huntId,
  });

  if (isLoading) {
    return (
      <div className="min-h-[50vh] flex items-center justify-center">
        <div className="animate-pulse text-text-light">Loading leaderboard...</div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-[50vh] flex items-center justify-center">
        <div className="text-red-600">Failed to load leaderboard</div>
      </div>
    );
  }

  return (
    <div className="max-w-3xl mx-auto px-4 py-12">
      <h1 className="text-3xl font-bold text-primary mb-8">Leaderboard</h1>

      {data?.entries.length === 0 ? (
        <div className="card text-center py-12">
          <p className="text-text-light">No completions yet. Be the first!</p>
        </div>
      ) : (
        <div className="card overflow-hidden">
          <table className="w-full">
            <thead className="bg-cream">
              <tr>
                <th className="px-4 py-3 text-left text-sm font-semibold text-text-dark">Rank</th>
                <th className="px-4 py-3 text-left text-sm font-semibold text-text-dark">Player</th>
                <th className="px-4 py-3 text-right text-sm font-semibold text-text-dark">Time</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {data?.entries.map((entry, index) => (
                <tr
                  key={`${entry.displayName}-${index}`}
                  className={index === 0 ? 'bg-yellow-50' : ''}
                >
                  <td className="px-4 py-3">
                    <span className={`font-bold ${index === 0 ? 'text-yellow-600' : 'text-text-dark'}`}>
                      #{entry.rank}
                    </span>
                    {index === 0 && <span className="ml-2">🏆</span>}
                  </td>
                  <td className="px-4 py-3 font-medium text-text-dark">
                    {entry.displayName}
                    {entry.prizeEligible && (
                      <span className="ml-2 text-xs text-secondary">Prize Eligible</span>
                    )}
                  </td>
                  <td className="px-4 py-3 text-right font-mono text-text-light">
                    {formatLeaderboardTime(entry.completionTimeMs)}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
